import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

const VOCAVisualization = ({ analyticsResults, loading = false }) => {
  // Chart refs
  const netSentimentRef = useRef(null);
  const customerHealthRef = useRef(null);
  const npsRef = useRef(null);
  const urgencyDonutRef = useRef(null);
  const painPointsBarRef = useRef(null);
  const sentimentDistRef = useRef(null);
  const intentDistRef = useRef(null);
  const topicSentimentRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedTable, setSelectedTable] = useState('insights');

  // Initialize all charts
  useEffect(() => {
    if (!analyticsResults || loading) return;

    const initializeCharts = () => {
      // Gauge Charts - Handle direct API response format
      const executiveData = analyticsResults.executiveDashboard?.analytics_result ||
        analyticsResults.executiveDashboard;
      if (executiveData?.performance_indicators) {
        const kpis = executiveData.performance_indicators;

        // Net Sentiment Gauge
        if (netSentimentRef.current) {
          const chart = echarts.init(netSentimentRef.current);
          chart.setOption(createGaugeOption((kpis.net_sentiment_score + 100) / 2, 'Net Sentiment', getSentimentColor(kpis.net_sentiment_score)));
        }

        // Customer Health Gauge  
        if (customerHealthRef.current) {
          const chart = echarts.init(customerHealthRef.current);
          chart.setOption(createGaugeOption(kpis.customer_health_score, 'Customer Health', '#3b82f6'));
        }

        // NPS Gauge
        if (npsRef.current) {
          const chart = echarts.init(npsRef.current);
          chart.setOption(createGaugeOption(kpis.nps_estimate, 'NPS Estimate', '#10b981'));
        }
      }

      // Urgency Distribution Donut
      const operationalData = analyticsResults.operationalDashboard?.analytics_result ||
        analyticsResults.operationalDashboard;
      if (operationalData?.urgency_analysis && urgencyDonutRef.current) {
        const urgencyData = operationalData.urgency_analysis.urgency_distribution;
        const chart = echarts.init(urgencyDonutRef.current);
        chart.setOption(createDonutOption(urgencyData, 'Urgency Distribution'));
      }

      // Pain Points Bar Chart
      if (operationalData?.pain_point_analysis && painPointsBarRef.current) {
        const painPoints = operationalData.pain_point_analysis.top_pain_points;
        const chart = echarts.init(painPointsBarRef.current);
        chart.setOption(createBarOption(painPoints, 'Top Pain Points'));
      }

      // Sentiment Distribution
      const enhancedData = analyticsResults.enhancedResults?.metadata?.analysis_summary ||
        analyticsResults.enhancedResults;
      if (enhancedData?.sentiment_distribution && sentimentDistRef.current) {
        const sentimentData = enhancedData.sentiment_distribution;
        const chart = echarts.init(sentimentDistRef.current);
        chart.setOption(createDonutOption(sentimentData, 'Sentiment Distribution'));
      }

      // Intent Distribution
      if (enhancedData?.intent_distribution && intentDistRef.current) {
        const intentData = enhancedData.intent_distribution;
        const chart = echarts.init(intentDistRef.current);
        chart.setOption(createDonutOption(intentData, 'Intent Distribution'));
      }

      // Topic Sentiment Analysis
      const topicData = analyticsResults.topicInsights?.analytics_result ||
        analyticsResults.topicInsights;
      if (topicData?.topic_insights && topicSentimentRef.current) {
        const topics = topicData.topic_insights;
        const chart = echarts.init(topicSentimentRef.current);
        chart.setOption(createTopicSentimentOption(topics));
      }
    };

    const timer = setTimeout(initializeCharts, 100);
    return () => clearTimeout(timer);
  }, [analyticsResults, loading]);

  // Chart resize handler
  useEffect(() => {
    const handleResize = () => {
      const charts = [
        netSentimentRef.current,
        customerHealthRef.current,
        npsRef.current,
        urgencyDonutRef.current,
        painPointsBarRef.current,
        sentimentDistRef.current,
        intentDistRef.current,
        topicSentimentRef.current
      ];

      charts.forEach(ref => {
        if (ref) {
          const chart = echarts.getInstanceByDom(ref);
          if (chart) chart.resize();
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper functions
  const getSentimentColor = (score) => {
    if (score > 30) return '#22c55e';
    if (score < -30) return '#ef4444';
    return '#f59e0b';
  };

  const createGaugeOption = (value, title, color) => ({
    title: {
      text: title,
      left: 'center',
      top: '85%',
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: axisColor
      }
    },
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      center: ['50%', '60%'],
      radius: '90%',
      min: 0,
      max: 100,
      splitNumber: 8,
      axisLine: {
        lineStyle: {
          width: 6,
          color: [[0.3, '#ef4444'], [0.7, '#f59e0b'], [1, '#22c55e']]
        }
      },
      pointer: {
        icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
        length: '12%',
        width: 20,
        offsetCenter: [0, '-60%'],
        itemStyle: {
          color: color
        }
      },
      axisTick: {
        length: 12,
        lineStyle: {
          color: 'auto',
          width: 2
        }
      },
      splitLine: {
        length: 20,
        lineStyle: {
          color: 'auto',
          width: 5
        }
      },
      axisLabel: {
        color: axisColor,
        fontSize: 10,
        distance: -60,
        rotate: 'tangential',
        formatter: function (value) {
          if (value === 87.5) {
            return 'Good';
          } else if (value === 12.5) {
            return 'Bad';
          }
          return '';
        }
      },
      title: {
        offsetCenter: [0, '-10%'],
        fontSize: 20,
        textStyle: { color: axisColor }
      },
      detail: {
        fontSize: 30,
        offsetCenter: [0, '-35%'],
        valueAnimation: true,
        formatter: function (value) {
          return Math.round(value);
        },
        color: color
      },
      data: [{
        value: value,
        name: 'Score'
      }]
    }]
  });

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#ccc' : '#333';

  const createDonutOption = (data, title) => ({
    title: {
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: axisColor
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      bottom: 20,
      textStyle: {
        fontSize: 12,
        color: axisColor
      }
    },
    series: [{
      name: title,
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['60%', '50%'],
      avoidLabelOverlap: false,
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 20,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: Object.entries(data).map(([key, value], index) => ({
        value: value,
        name: key.charAt(0).toUpperCase() + key.slice(1),
        itemStyle: {
          color: getColorByIndex(index)
        }
      }))
    }]
  });

  const createBarOption = (data, title) => ({
    title: {
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: axisColor
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      boundaryGap: [0, 0.01],
      axisLabel: { fontSize: 10, color: axisColor }
    },
    yAxis: {
      type: 'category',
      data: Object.keys(data).map(key => key.charAt(0).toUpperCase() + key.slice(1)),
      axisLabel: { fontSize: 10, color: axisColor }
    },
    series: [{
      name: 'Frequency',
      type: 'bar',
      data: Object.values(data),
      itemStyle: {
        color: '#3b82f6'
      },
      barWidth: '60%'
    }]
  });

  const createTopicSentimentOption = (topics) => ({
    title: {
      text: 'Topic Sentiment Analysis',
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: axisColor
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999'
        }
      }
    },
    legend: {
      data: ['Frequency', 'Sentiment Score'],
      top: 50,
      textStyle: { color: axisColor }
    },
    xAxis: [{
      type: 'category',
      data: topics.map(topic => topic.topic.charAt(0).toUpperCase() + topic.topic.slice(1)),
      axisPointer: {
        type: 'shadow'
      },
      axisLabel: {
        color: axisColor
      }
    }],
    yAxis: [
      {
        type: 'value',
        name: 'Frequency',
        min: 0,
        axisLabel: {
          formatter: '{value}',
          color: axisColor
        }
      },
      {
        type: 'value',
        name: 'Sentiment',
        min: -1,
        max: 1,
        axisLabel: {
          formatter: '{value}',
          color: axisColor
        }
      }
    ],
    series: [
      {
        name: 'Frequency',
        type: 'bar',
        data: topics.map(topic => topic.frequency),
        itemStyle: {
          color: '#3b82f6'
        }
      },
      {
        name: 'Sentiment Score',
        type: 'line',
        yAxisIndex: 1,
        data: topics.map(topic => topic.sentiment_score),
        itemStyle: {
          color: '#ef4444'
        },
        lineStyle: {
          width: 3
        }
      }
    ]
  });

  const getColorByIndex = (index) => {
    const colors = ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
    return colors[index % colors.length];
  };

  // Table data preparation
  const getTableData = (data = analyticsResults) => {
    switch (selectedTable) {
      case 'insights':
        const topicData = data.topicInsights?.analytics_result || data.topicInsights;
        return topicData?.topic_insights || [];
      case 'recommendations':
        const recData = data.recommendations?.analytics_result || data.recommendations;
        return recData?.recommendations || [];
      case 'escalation':
        const opData = data.operationalDashboard?.analytics_result || data.operationalDashboard;
        return opData?.escalation_queue || [];
      default:
        return [];
    }
  };

  const renderTableContent = (sourceData = analyticsResults) => {
    const data = getTableData(sourceData);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = data.slice(startIndex, endIndex);

    if (selectedTable === 'insights') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Topic</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sentiment Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Urgency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Confidence</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {currentData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {item.topic.charAt(0).toUpperCase() + item.topic.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.frequency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.sentiment_score > 0.3 ? 'bg-green-100 text-green-800' :
                      item.sentiment_score < -0.3 ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                      {item.sentiment_score.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {Object.entries(item.urgency_distribution).map(([key, value]) => (
                      <span key={key} className="inline-block mr-2">
                        {key}: {value}
                      </span>
                    ))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {(item.average_confidence * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedTable === 'recommendations') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Impact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Effort</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Win</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {currentData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                    <div>
                      <div className="font-semibold">{item.title}</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{item.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.priority === 'critical' || item.priority === 'high' ? 'bg-red-100 text-red-800' :
                      item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                      {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.business_impact.charAt(0).toUpperCase() + item.business_impact.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.implementation_effort.charAt(0).toUpperCase() + item.implementation_effort.slice(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.quick_win ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        No
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedTable === 'escalation') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Urgency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {currentData.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.urgency_level === 'high' || item.urgency_level === 'critical' ? 'bg-red-100 text-red-800' :
                      item.urgency_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                      {item.urgency_level.charAt(0).toUpperCase() + item.urgency_level.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {item.primary_intent.charAt(0).toUpperCase() + item.primary_intent.slice(1)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div>
                      <div className="font-medium">{item.summary}</div>
                      <div className="text-xs text-gray-400 mt-1">"{item.customer_comment.substring(0, 100)}..."</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <ul className="text-xs space-y-1">
                      {item.actionable_items.slice(0, 2).map((action, actionIndex) => (
                        <li key={actionIndex}>• {action.substring(0, 50)}...</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  const totalPages = Math.ceil(getTableData().length / itemsPerPage);

  // Function to render the main visualization content
  const renderMainVisualizationContent = (data) => {
    return (
      <div className="space-y-6">
        {/* Executive KPI Gauges */}
        {(data.executiveDashboard?.analytics_result || data.executiveDashboard) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">📊 Executive KPI Dashboard</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={netSentimentRef} style={{ width: '100%', height: '250px' }}></div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={customerHealthRef} style={{ width: '100%', height: '250px' }}></div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={npsRef} style={{ width: '100%', height: '250px' }}></div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Operational Charts */}
        {(data.operationalDashboard?.analytics_result || data.operationalDashboard) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">🎯 Operational Analytics</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={urgencyDonutRef} style={{ width: '100%', height: '300px' }}></div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={painPointsBarRef} style={{ width: '100%', height: '300px' }}></div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sentiment & Intent Analysis */}
        {(data.enhancedResults?.metadata?.analysis_summary || data.enhancedResults) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">💭 Sentiment & Intent Analysis</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={sentimentDistRef} style={{ width: '100%', height: '300px' }}></div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div ref={intentDistRef} style={{ width: '100%', height: '300px' }}></div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Topic Analysis */}
        {(data.topicInsights?.analytics_result || data.topicInsights) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">🏷️ Topic Sentiment Analysis</h3>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div ref={topicSentimentRef} style={{ width: '100%', height: '400px' }}></div>
            </div>
          </motion.div>
        )}

        {/* Data Tables with Pagination */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6"
        >
          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6">📋 Detailed Data Tables</h3>

          {/* Table Selection */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => { setSelectedTable('insights'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium ${selectedTable === 'insights'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              Topic Insights
            </button>
            <button
              onClick={() => { setSelectedTable('recommendations'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium ${selectedTable === 'recommendations'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              Recommendations
            </button>
            <button
              onClick={() => { setSelectedTable('escalation'); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium ${selectedTable === 'escalation'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              Escalation Queue
            </button>
          </div>

          {/* Table Content */}
          {renderTableContent(data)}

          {/* Pagination */}
          {Math.ceil(getTableData(data).length / itemsPerPage) > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getTableData(data).length)} of {getTableData(data).length} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(Math.ceil(getTableData(data).length / itemsPerPage))].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`px-3 py-1 rounded border text-sm ${currentPage === index + 1
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(getTableData(data).length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(getTableData(data).length / itemsPerPage)}
                  className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading VOCA visualizations...</p>
        </div>
      </div>
    );
  }

  if (!analyticsResults || Object.keys(analyticsResults).length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-blue-600 dark:text-blue-400 text-2xl mr-3">📊</div>
            <div>
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">No VOCA Analytics Available</h3>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                Complete a VOCA training task and wait for analytics generation to view your data visualizations.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return renderMainVisualizationContent(analyticsResults);
};

export default VOCAVisualization; 